<template>
    <GenericModal
        v-on:close="emitCloseModal"
        v-if="modalVisible"
    >
        <template v-slot:header>
            <h2>App Settings</h2>
        </template>
        <template v-slot:body>
            <div class="modal-body">
                <!-- <Accordion>
                    <template v-slot:header>
                        <h3>Offline Capabilities</h3>
                    </template>
                    <template v-slot:body>
                        <p>Download pages deemed important for offline access.</p>
                        <button class="btn btn-primary">
                            Download
                        </button>
                    </template>
                </Accordion> -->
                <Accordion :initially-expanded="true">
                    <template v-slot:header>
                        <h3>Push Notifications</h3>
                    </template>
                    <template v-slot:body>
                        <p>Determine whether push notifications are set up properly</p>
                        <div class="d-flex flex-row justify-content-between align-items-center">
                            <h4>Notification Permission Status</h4>
                            <div class="badge" :class="badgeClassForStatus">
                                {{ notificationPermissionUserDisplay }}
                            </div>
                        </div>

                        <div class="d-flex flex-column justify-content-between align-items-center" v-if="userDeniedNotificationPermission">
                            <p>
                                Looks like you have denied push notifications on this device. To enable them go to the settings for this app, toggle them on, and then close and reopen the app.
                            </p>
                        </div>
            

                        <div class="d-flex flex-column justify-content-between align-items-center" v-if="userPromptableForWebPushPermissions">
                            <p>
                                You have not yet enabled push notifications on this device. 
                                Click the button below to enable them.
                            </p>
                            <button class="btn btn-primary" v-if="userPromptableForWebPushPermissions" v-on:click="promptUserForPushNotifications">
                                Enable Push Notifications
                            </button>
                        </div>

                        <!-- iterate over webPushSubscriptions to make UserWebPushSubscriptionCard -->
                        <div v-if="postingSubscription">
                            <div class="d-flex flex-column justify-content-between align-items-center">
                                <p>Setting up push notifications...</p>
                            </div>
                        </div>
                        <div v-if="userGrantedNotificationPermission && !postingSubscription">
                            <h4>Active Push Subscriptions</h4>
                            <p class="small">Below is your active push subscription for this device. If you either do not see a subscription below or when you press Send Test Notification do not receive it, please check the notifications permission in app settings. If notifications are disabled please turn them on, then close and reopen the app.</p>

                            <UserWebPushSubscriptionCard 
                                v-if="!fetchingWebPushSubscriptions && subscription.endpoint === activeSubscription.endpoint"
                                v-for="subscription in webPushSubscriptions" 
                                v-on:subscription-expired="fetchWebPushSubscriptions"
                                :key="subscription.id"
                                :active="activeSubscription && subscription.endpoint === activeSubscription.endpoint"
                                :subscription="subscription" 
                            />
                        </div>

                        <div v-if="userGrantedNotificationPermission && fetchingWebPushSubscriptions">
                            <BSpinner />
                        </div>
                    

                    </template>
                </Accordion>
            </div>
        </template>
    </GenericModal>
</template>



<script>
    import axios from 'axios';
    import GenericModal from './GenericModal.vue';
    import Accordion from './Accordion.vue';
    import UserWebPushSubscriptionCard from './UserWebPushSubscriptionCard.vue';
    import pwaUtils from '../pwaUtils.js'
    import { BSpinner } from 'bootstrap-vue';

    var toastConfig = {
        duration: 4000,
        position: "bottom-right",
        action: {
            text: "DISMISS",
            onClick: (e, toastObject) => {
                toastObject.goAway(0);
            },
        },
    };

    export default {
        components: {
            GenericModal,
            Accordion,
            UserWebPushSubscriptionCard,
            BSpinner
        },

        props: {
            eventAttendeeId: {
                required: true,
            },
            modalVisible: {
                type: Boolean,
                required: true,
            }
        },

        data: function () {
            return {
                webPushSubscriptions: [],
                notificationPermissionStatus: null,
                localStoragePushSubscriptionEndpoint: null,
                postingSubscription: false,
                timeoutId: null,
                activeSubscription: null,
                fetchingWebPushSubscriptions: false
            };
        },

        computed: {
            userDeniedNotificationPermission () {
                return this.notificationPermissionStatus === 'denied';
            },

            userPromptableForWebPushPermissions () {
                return this.notificationPermissionStatus === 'prompt' || this.notificationPermissionStatus === 'default';
            },

            userGrantedNotificationPermission () {
                return this.notificationPermissionStatus === 'granted';
            },

            notificationPermissionUserDisplay () {
                if (this.userDeniedNotificationPermission) {
                    return 'Denied';
                } else if (this.userPromptableForWebPushPermissions) {
                    return 'Promptable';
                } else if (this.userGrantedNotificationPermission) {
                    return 'Granted';
                }
            },

            badgeClassForStatus () {
                if (this.userDeniedNotificationPermission) {
                    return 'badge-danger';
                } else if (this.userPromptableForWebPushPermissions) {
                    return 'badge-warning';
                } else if (this.userGrantedNotificationPermission) {
                    return 'badge-success';
                }
            }
        },

        watch: {
            modalVisible (newVal) {
                if (newVal) {
                    this.fetchWebPushSubscriptions();
                    this.setActiveSubscription();
                    this.checkNotificationPermission();
                    this.startNotificationPermissionPoll();
                } else {
                    this.stopNotificationPermissionPoll();
                }
            },

            // makes unnecessary duplicate request that maybe causing race condition with read only database
            // guessing that it is not in the read and then tries to write causing the sql error
            // userGrantedNotificationPermission (newVal, oldVal) {
            //     if (newVal) {
            //         window.serviceWorkerHelpers.updateOrCreatePushSubscription();
            //     }
            // }
        },

        methods: {

            listenToUpdatedOrNewPushSubscriptionEvent () {
                // listen to this event window.dispatchEvent(new CustomEvent('push-subscription-registered', {detail: res}))
                window.addEventListener('push-subscription-registered', (event) => {
                    console.log('push-subscription-registered event', event);
                    this.setActiveSubscription();
                    this.fetchWebPushSubscriptions();
                });
            },

            showNavItemButtons () {
                var els = document.querySelectorAll('.nav-id-app-settings')
                // iterate over els and remove class hidden
                els.forEach(el => {
                    el.classList.remove('hidden');
                });
            },

            emitCloseModal () {
                this.$emit('close');
            },

            async setActiveSubscription () {
                // get service worker registration using await syntax
                var registration = await navigator.serviceWorker.getRegistration();
                // get subscription using registration object
                var subscription = await registration.pushManager.getSubscription();
                // set subscription to local storage
                console.log('setting active subscription', subscription);
                this.activeSubscription = subscription;
            },

            checkNotificationPermission () {
                if (!('Notification' in window)) {
                    return;
                }
                this.changeNotificationPermissionStatus(Notification.permission)
            },

            // make a poll that checks the notification permission status every 5 seconds using setTimeout, and continuously update the setTimeout id
            startNotificationPermissionPoll () {
                this.timeoutId = setTimeout(() => {
                    this.checkNotificationPermission();
                    this.startNotificationPermissionPoll();
                }, 5000);
            },

            stopNotificationPermissionPoll () {
                console.log('stopping notification permission poll');
                clearTimeout(this.timeoutId);
            },

            changeNotificationPermissionStatus (permissionStatus) {
                this.notificationPermissionStatus = permissionStatus
            },

            async promptUserForPushNotifications () {
                if (this.postingSubscription) {
                    return;
                }
                this.postingSubscription = true;
                try {
                    var res = await window.serviceWorkerHelpers.handleAllowedPushNotificationPrompt();
                    if (res && res.data) {
                        this.webPushSubscriptions.unshift(res.data);
                    } else {
                        toastr.error('Failed to set up push notifications, you may have denied them in settings.', toastConfig);
                        this.changeNotificationPermissionStatus(Notification.permission)
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    this.postingSubscription = false;
                }
                

            },

            fetchWebPushSubscriptions () {
                this.fetchingWebPushSubscriptions = true;

                axios.get(`/api/v1/event-attendees/${this.eventAttendeeId}/push-subscriptions`)
                    .then(response => {
                        this.webPushSubscriptions = response.data;
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .finally(() => {
                        this.fetchingWebPushSubscriptions = false;
                        this.$forceUpdate();
                    });

            },
        },

        mounted() {
            
            this.fetchWebPushSubscriptions();
            this.checkNotificationPermission();
            this.setActiveSubscription();
            this.listenToUpdatedOrNewPushSubscriptionEvent();
            window.pwaUtils = pwaUtils;
            var isPWA = pwaUtils.isPWA();
            if (isPWA) {
                this.showNavItemButtons();
            } else {
                console.log('not opened as a PWA');
            }

        }
    }

</script>